import { useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  Save,
  Trash2,
  AlertTriangle,
  Lock,
  Eye,
  EyeOff,
  Plus,
  Copy,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/Card';
import { ConfirmModal } from '@/components/ui/Modal';
import { useToast } from '@/contexts/ToastContext';
import { useUpdateProject, useDeleteProject } from '@/hooks/useProject';
import { ProjectGitHubPanel } from '@/components/features/github/ProjectGitHubPanel';
import type { Project, ProjectVisibility } from '@/types';

export function ProjectSettingsPage() {
  const { project } = useOutletContext<{ project: Project }>();
  const navigate = useNavigate();
  const toast = useToast();
  const updateProject = useUpdateProject(project.slug);
  const deleteProject = useDeleteProject();

  const [formData, setFormData] = useState({
    name: project.name,
    description: project.description || '',
    visibility: project.visibility,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const handleSave = async () => {
    try {
      await updateProject.mutateAsync(formData);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProject.mutateAsync({ slug: project.slug, id: project.id });
      toast.success('Project deleted', `${project.name} has been permanently deleted`);
      navigate('/dashboard');
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className="h-full p-6 max-w-4xl mx-auto space-y-6 overflow-y-auto">
      {/* General Settings */}
      <Card>
        <CardHeader>
          <CardTitle>General Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label="Project Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />

          <Textarea
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Describe your project..."
          />

          <Select
            label="Visibility"
            value={formData.visibility}
            onChange={(e) => setFormData({ ...formData, visibility: e.target.value as ProjectVisibility })}
            options={[
              { value: 'Private', label: 'Private - Only you can access' },
              { value: 'Public', label: 'Public - Anyone can view' },
            ]}
          />

          <div className="pt-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Project Slug
            </label>
            <div className="flex items-center gap-2">
              <Input value={project.slug} disabled className="font-mono" />
              <CopyButton text={project.slug} />
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              This is used in URLs and cannot be changed
            </p>
          </div>
        </CardContent>
        <CardFooter>
          <Button
            onClick={handleSave}
            isLoading={updateProject.isPending}
            leftIcon={<Save className="w-4 h-4" />}
          >
            Save Changes
          </Button>
        </CardFooter>
      </Card>

      {/* GitHub Integration */}
      <ProjectGitHubPanel projectId={project.id} projectSlug={project.slug} />

      {/* Environment Variables */}
      <EnvironmentVariablesSection projectId={project.id} />

      {/* Danger Zone */}
      <Card className="border-red-200 dark:border-red-900">
        <CardHeader>
          <CardTitle className="text-red-600 dark:text-red-400 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white">
                Delete Project
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Permanently delete this project and all its data. This action cannot be undone.
              </p>
            </div>
            <Button
              variant="danger"
              onClick={() => setShowDeleteModal(true)}
              leftIcon={<Trash2 className="w-4 h-4" />}
            >
              Delete
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteConfirmation('');
        }}
        onConfirm={handleDelete}
        title="Delete Project"
        message={
          <div className="space-y-4">
            <p>
              This will permanently delete <strong>{project.name}</strong> and all associated data including:
            </p>
            <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400">
              <li>All project files and code</li>
              <li>Docker containers and images</li>
              <li>Environment variables</li>
              <li>Custom domains</li>
              <li>Database (if any)</li>
              <li>AI conversation history</li>
            </ul>
            <div className="pt-2 border-t border-gray-200 dark:border-dark-700">
              <Input
                label={
                  <span>
                    Type <code className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded font-mono text-sm">{project.name}</code> to confirm
                  </span>
                }
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder={project.name}
                className={deleteConfirmation === project.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
              />
              {deleteConfirmation.length > 0 && deleteConfirmation !== project.name && (
                <p className="mt-1 text-sm text-red-500">Project name doesn't match</p>
              )}
            </div>
          </div>
        }
        confirmText="I understand, delete this project"
        variant="danger"
        isLoading={deleteProject.isPending}
        confirmDisabled={deleteConfirmation !== project.name}
      />
    </div>
  );
}

function EnvironmentVariablesSection({ projectId: _projectId }: { projectId: string }) {
  const [variables, setVariables] = useState([
    { id: '1', key: 'DATABASE_URL', value: 'postgresql://...', isSecret: true },
    { id: '2', key: 'API_KEY', value: 'sk-...', isSecret: true },
    { id: '3', key: 'NODE_ENV', value: 'production', isSecret: false },
  ]);
  const [showSecrets, setShowSecrets] = useState<Set<string>>(new Set());

  const toggleSecret = (id: string) => {
    setShowSecrets((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const addVariable = () => {
    const newVar = { id: Date.now().toString(), key: '', value: '', isSecret: false };
    setVariables([...variables, newVar]);
  };

  const removeVariable = (id: string) => {
    setVariables(variables.filter((v) => v.id !== id));
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Lock className="w-5 h-5" />
          Environment Variables
        </CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={addVariable}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          Add Variable
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {variables.map((variable) => (
            <div
              key={variable.id}
              className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-dark-900 rounded-lg"
            >
              <Input
                value={variable.key}
                onChange={(e) =>
                  setVariables(
                    variables.map((v) =>
                      v.id === variable.id ? { ...v, key: e.target.value } : v
                    )
                  )
                }
                placeholder="KEY"
                className="font-mono flex-1"
              />
              <span className="text-gray-400">=</span>
              <div className="relative flex-1">
                <Input
                  type={variable.isSecret && !showSecrets.has(variable.id) ? 'password' : 'text'}
                  value={variable.value}
                  onChange={(e) =>
                    setVariables(
                      variables.map((v) =>
                        v.id === variable.id ? { ...v, value: e.target.value } : v
                      )
                    )
                  }
                  placeholder="value"
                  className="font-mono pr-10"
                />
                {variable.isSecret && (
                  <button
                    onClick={() => toggleSecret(variable.id)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showSecrets.has(variable.id) ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
              <Switch
                checked={variable.isSecret}
                onChange={(checked) =>
                  setVariables(
                    variables.map((v) =>
                      v.id === variable.id ? { ...v, isSecret: checked } : v
                    )
                  )
                }
                label="Secret"
                size="sm"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeVariable(variable.id)}
                className="text-red-500 hover:text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
        {variables.length === 0 && (
          <p className="text-center text-gray-500 dark:text-gray-400 py-8">
            No environment variables configured
          </p>
        )}
      </CardContent>
      <CardFooter>
        <Button leftIcon={<Save className="w-4 h-4" />}>Save Variables</Button>
      </CardFooter>
    </Card>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button variant="outline" size="sm" onClick={handleCopy}>
      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
    </Button>
  );
}
